import { Loading } from '@monorepo/shared/components/Loading';
import { GenericError } from '@monorepo/shared/componentsV2/errorDisplays/GenericError';
import { fieldValueDisplayFactory } from '@monorepo/shared/componentsV2/fieldDataType/values/fieldValueDisplayFactory';
import { useSkipWidgetLink } from '@monorepo/shared/componentsV2/SkipWidgetLink';
import { BulkDeleteLimitWarning } from '@monorepo/shared/componentsV2/Table/BulkDeleteLimitWarning';
import {
  Pagination,
  PaginationOnPageChange,
} from '@monorepo/shared/componentsV2/Table/Pagination';
import { Table } from '@monorepo/shared/componentsV2/Table/Table';
import { TableBulkActions } from '@monorepo/shared/componentsV2/Table/TableBulkActions';
import { TableColumn } from '@monorepo/shared/componentsV2/Table/types';
import { useHandleBulkDelete } from '@monorepo/shared/componentsV2/Table/useHandleBulkDelete';
import { useTableBulkActions } from '@monorepo/shared/componentsV2/Table/useTableBulkActions';
import { useHasOrgUpdatePermissionOrSuperAdmin } from '@monorepo/shared/hooks/permissions/useHasPermissions';
import { useResources } from '@monorepo/shared/hooks/resources/useResources';
import { useResourceTypeWithProperties } from '@monorepo/shared/hooks/resources/useResourceTypeWithProperties';
import { useDebouncedValue } from '@monorepo/shared/hooks/useDebouncedValue';
import { usePrevious } from '@monorepo/shared/hooks/usePrevious';
import {
  fillHeightAndScrollable,
  verticalLayout,
} from '@monorepo/shared/styles/layout';
import { format } from 'date-fns';
import {
  FieldDataType,
  FieldResponse,
  FieldValueVersion,
  ResourceResponse,
  ResourceTypeRefResponse,
  SortDirection,
} from 'mapistry-shared';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useResourcesBulkDelete } from '../../hooks/useResourcesBulkDelete';
import { FieldVersionHistoryModal } from '../ResourceDetails/FieldVersionHistoryModal';
import {
  ValueWithHistory,
  ValueWithHistoryColumnHeader,
} from '../ValueWithHistory';
import { ViewResourcesActionMenu } from './ViewResourcesActionMenu';

const StyledContainer = styled.div`
  ${verticalLayout}
`;

const AboveTableContent = styled.div`
  display: flex;
  justify-content: space-between;
  padding-left: 1.625rem;
`;

const StyledTable = styled(Table)`
  ${fillHeightAndScrollable}
` as typeof Table;

type OpenedFieldHistoryInfo = {
  fieldName: string;
  fieldType: FieldDataType;
  resourceName: string;
  versions: FieldValueVersion[];
};

function getTableColumns({
  isActionMenuVisible,
  nameColumnContents,
  onEditResource,
  onShowHistory,
  resourceProperties,
  resourceType,
}: {
  isActionMenuVisible: boolean;
  nameColumnContents?: ViewResourcesTableProps['nameColumnContents'];
  onEditResource: ViewResourcesTableProps['onEditResource'];
  onShowHistory: (history: OpenedFieldHistoryInfo) => void;
  resourceProperties: FieldResponse[];
  resourceType: ResourceTypeRefResponse;
}): TableColumn<ResourceResponse>[] {
  const nameCol: TableColumn<ResourceResponse> = {
    header: 'Name',
    contents: nameColumnContents || 'name',
    id: 'name',
  };

  const siteAssocCol: TableColumn<ResourceResponse> = {
    header: 'Site Association',
    contents: (row) => row.project?.name || '',
    id: 'siteAssociation',
  };

  const createdAtCol: TableColumn<ResourceResponse> = {
    header: 'Created',
    // no localEquivalentOfUTC because this value is NOT a wall clock time, it is truly in UTC time
    contents: (row) => format(new Date(row.createdAt), 'Pp'),
    id: 'createdAt',
  };

  const actionMenuCol: TableColumn<ResourceResponse> = {
    header: 'Actions',
    contents: (row: ResourceResponse) => (
      <ViewResourcesActionMenu resource={row} onEditResource={onEditResource} />
    ),
    id: 'actionMenu',
    width: '10%',
    align: 'center',
  };

  const resourcePropertyCols = (resourceProperties || []).map((field) => ({
    header: field.isVersioned ? (
      <ValueWithHistoryColumnHeader label={field.name} />
    ) : (
      field.name
    ),
    contents: (row: ResourceResponse) => {
      const fieldValue = row.propertyValues[field.id];
      const latestValue = fieldValue?.fieldValue;
      const displayValue = latestValue
        ? fieldValueDisplayFactory({
            fieldType: field.type,
            fieldValue: latestValue,
          })
        : '';
      if (!field.isVersioned) {
        return displayValue;
      }
      const versions = fieldValue?.valueVersions ?? [];
      return (
        <ValueWithHistory
          displayValue={displayValue}
          fieldName={field.name}
          onShowHistory={() =>
            onShowHistory({
              fieldName: field.name,
              fieldType: field.type,
              resourceName: row.name,
              versions,
            })
          }
          versions={versions}
        />
      );
    },

    id: field.id,
  }));

  return [
    nameCol,
    ...(resourceType?.isSiteSpecific ? [siteAssocCol] : []),
    ...resourcePropertyCols,
    createdAtCol,
    ...(isActionMenuVisible ? [actionMenuCol] : []),
  ];
}

type ViewResourcesTableProps = {
  addResourceButtons?: React.ReactNode;
  filterBy?: string;
  onEditResource: (resourceId: string) => void;
  onPageChange: PaginationOnPageChange;
  // by default, the resource name column will simply print the name of the resource
  // this prop lets you override that behavior
  nameColumnContents?: TableColumn<ResourceResponse>['contents'];
  pageNumber: number;
};

export function ViewResourcesTable({
  addResourceButtons,
  filterBy = '',
  onEditResource,
  onPageChange,
  nameColumnContents,
  pageNumber,
}: ViewResourcesTableProps) {
  const { resourceTypeId, organizationId } = useParams();
  if (!resourceTypeId || !organizationId) {
    throw new Error(
      "ViewResourcesTable component was rendered in a page with url that doesn't have resourceTypeId or organizationId",
    );
  }
  const canEditOrgSettings =
    useHasOrgUpdatePermissionOrSuperAdmin(organizationId);

  const [openedFieldHistoryInfo, setOpenedFieldHistoryInfo] =
    useState<OpenedFieldHistoryInfo | null>(null);

  // Debounce updates to name filter, so we send server request only after
  // a user pauses typing for at least half a second
  const nameFilter = useDebouncedValue(filterBy);
  const previousNameFilter = usePrevious(nameFilter);
  useEffect(() => {
    // Skip initial mount — onPageChange navigates and would add a history entry
    if (previousNameFilter === undefined) return;

    // Reset page number when filter changes
    onPageChange(0);

    // Not including `onPageChange` in useEffect's dependencies, because we need to invoke this effect only
    // when filter updates. Every time we paginate we navigate to new url, `navigate` object updates,
    // which updates `onPageChange` and would've invoked this hook too.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameFilter]);

  const {
    resources,
    pagination,
    isLoading: isLoadingResources,
    isFetching: isFetchingResources,
  } = useResources({
    nameFilter,
    organizationId,
    resourceTypeId,
    requestedPage: pageNumber,
    sortDirection: SortDirection.ASC,
  });
  const {
    resourceType,
    resourceProperties,
    isLoading: isLoadingResourceType,
  } = useResourceTypeWithProperties({
    organizationId,
    resourceTypeId,
  });

  const {
    getIsRowSelected,
    isBulkActionsModeOn,
    onToggleRow,
    selectedIds,
    setVisibleRows,
  } = useTableBulkActions();
  useEffect(() => {
    setVisibleRows(resources || []);
  }, [resources, setVisibleRows]);

  const tableColumns = useMemo(
    () =>
      resourceType && resourceProperties
        ? getTableColumns({
            isActionMenuVisible: canEditOrgSettings && !isBulkActionsModeOn,
            nameColumnContents: isBulkActionsModeOn
              ? undefined
              : nameColumnContents,
            onEditResource,
            onShowHistory: setOpenedFieldHistoryInfo,
            resourceProperties,
            resourceType,
          })
        : [],
    [
      canEditOrgSettings,
      isBulkActionsModeOn,
      nameColumnContents,
      onEditResource,
      resourceProperties,
      resourceType,
    ],
  );

  const [bulkDeleter] = useResourcesBulkDelete({
    config: { throwOnError: true },
  });
  const onBulkDelete = useCallback(
    async () =>
      bulkDeleter({
        resourceIds: selectedIds,
        resourceTypeId,
        organizationId,
      }),
    [bulkDeleter, selectedIds, resourceTypeId, organizationId],
  );
  const { handleBulkDeleteClick, isBulkDeleting } = useHandleBulkDelete({
    onBulkDelete,
    pluralEntityLabel: 'Resources',
  });

  const { SkipLink, SkipLinkTarget } = useSkipWidgetLink();

  if (isLoadingResources || isFetchingResources || isLoadingResourceType) {
    return <Loading />;
  }

  if (!resources || !resourceProperties || !resourceType) {
    return <GenericError />;
  }

  return (
    <StyledContainer>
      <AboveTableContent>
        <TableBulkActions
          isBulkDeleting={isBulkDeleting}
          onDelete={handleBulkDeleteClick}
        />
        {!isBulkActionsModeOn && addResourceButtons}
      </AboveTableContent>
      <BulkDeleteLimitWarning pluralEntityLabel="Resources" />
      <SkipLink linkText="Skip to pagination" />
      <StyledTable
        ariaLabel="Resources"
        canRowsBeSelected={isBulkActionsModeOn}
        columns={tableColumns}
        getCheckboxAriaLabel={({ name }) => `Select Resource ${name}`}
        getIsRowSelected={getIsRowSelected}
        isRowSelectionDisabled={isBulkDeleting}
        onToggleRow={onToggleRow}
        rows={resources}
      />
      <SkipLinkTarget />
      <Pagination
        onPageChange={onPageChange}
        page={pagination?.currentPage || 0}
        rowsPerPage={pagination?.perPage}
        totalRowCount={pagination?.fullCount}
      />
      {openedFieldHistoryInfo && (
        <FieldVersionHistoryModal
          fieldName={openedFieldHistoryInfo.fieldName}
          fieldType={openedFieldHistoryInfo.fieldType}
          onClose={() => setOpenedFieldHistoryInfo(null)}
          resourceName={openedFieldHistoryInfo.resourceName}
          versions={openedFieldHistoryInfo.versions}
        />
      )}
    </StyledContainer>
  );
}
